import { useModal } from '@farmshare/ui-components';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';

import {
  type CutsheetQuestionnaireResponseByIdQuery,
  useCutsheetQuestionnaireResponseByIdQuery,
} from 'lib/graphql';
import {
  useCatalogState,
  useQuestionnaireState,
} from 'features/cutsheets/v2/hooks';
import {
  AdvancedViewStep,
  QuestionnaireResponseProvider,
} from 'features/cutsheets/v2/components/questionnaire-wizard';
import type {
  CutsheetCatalog,
  CutsheetQuestionnaire,
} from 'features/cutsheets/v2/types';

// ── Types ────────────────────────────────────────────────────────────────────

type FullResponse = NonNullable<
  CutsheetQuestionnaireResponseByIdQuery['cutsheetQuestionnaireResponseById']
>;

// ── Read-only preview content ─────────────────────────────────────────────────

interface ResponsePreviewContentProps {
  response: FullResponse;
  catalog: CutsheetCatalog;
  questionnaire: CutsheetQuestionnaire;
}

function ResponsePreviewContent({
  response,
  catalog,
  questionnaire,
}: ResponsePreviewContentProps) {
  return (
    <ReadOnlyAdvancedView
      response={response}
      catalog={catalog}
      questionnaire={questionnaire}
    />
  );
}

// ── Read-only advanced view ──────────────────────────────────────────────────

interface ReadOnlyAdvancedViewProps {
  response: FullResponse;
  catalog: CutsheetCatalog;
  questionnaire: CutsheetQuestionnaire;
}

/**
 * Renders the response inside `AdvancedViewStep` but blocks every interaction
 * via the `inert` attribute so checkboxes, selects, and text fields can't be
 * changed.  The provider is seeded via `defaultSelections` built from the
 * saved response so the visual state matches the persisted data.
 * @param root0
 * @param root0.response
 * @param root0.catalog
 * @param root0.questionnaire
 */
function ReadOnlyAdvancedView({
  response,
  catalog,
  questionnaire,
}: ReadOnlyAdvancedViewProps) {
  const seededQuestionnaire = useMemo<CutsheetQuestionnaire>(() => {
    const selectedCuts = (response.selectedCuts ?? [])
      .filter((c): c is NonNullable<typeof c> => Boolean(c))
      .map((c) => ({
        optionId: c.optionId,
        specifications: (c.specifications ?? [])
          .filter((s): s is NonNullable<typeof s> => Boolean(s))
          .map((s) => ({
            specification: s.specification,
            type: s.type as 'number' | 'text' | 'select' | 'multiselect',
            value: Array.isArray(s.value)
              ? (s.value as string[]).join(', ')
              : String(s.value ?? ''),
          })),
      }));

    const trimSelections = response.trimSelections
      ? {
          type: response.trimSelections.type as
            | 'ranked'
            | 'selection_by_percentage',
          trim: (response.trimSelections.trim ?? [])
            .filter((t): t is NonNullable<typeof t> => Boolean(t))
            .map((t) => {
              const trimSpecs = (t.specifications ?? [])
                .filter((s): s is NonNullable<typeof s> => Boolean(s))
                .map((s) => ({
                  specification: s.specification,
                  type: s.type as 'number' | 'text' | 'select' | 'multiselect',
                  value: Array.isArray(s.value)
                    ? (s.value as string[]).join(', ')
                    : String(s.value ?? ''),
                }));
              return {
                optionId: t.optionId,
                ...(t.rank != null
                  ? { rank: t.rank, quantity: t.quantity ?? 0 }
                  : { percentage: t.percentage ?? 0 }),
                ...(trimSpecs.length > 0 ? { specifications: trimSpecs } : {}),
              };
            }),
        }
      : undefined;

    const specifications = (response.specifications ?? [])
      .filter((s): s is NonNullable<typeof s> => Boolean(s))
      .map((s) => ({
        specification: s.specification,
        type: s.type as 'number' | 'text' | 'select' | 'multiselect',
        value: Array.isArray(s.value)
          ? (s.value as string[]).join(', ')
          : String(s.value ?? ''),
      }));

    return {
      ...questionnaire,
      defaultSelections: { selectedCuts, trimSelections, specifications },
    };
  }, [response, questionnaire]);

  return (
    <Box
      // `inert` blocks pointer + keyboard interaction on the whole subtree
      // without altering layout.  React passes unknown props through to DOM.
      {...({ inert: '' } as Record<string, unknown>)}
      sx={{
        cursor: 'default',
        // Belt-and-braces: gracefully handle browsers that don't support inert
        pointerEvents: 'none',
        userSelect: 'text',
      }}
    >
      <QuestionnaireResponseProvider
        questionnaire={seededQuestionnaire}
        catalog={catalog}
        onClose={() => {}}
        fixedCutsheetType="user"
      >
        <AdvancedViewStep
          disableNonQuestionnaireCuts={false}
          hideSpecSettings
        />
      </QuestionnaireResponseProvider>
    </Box>
  );
}

// ── Preview loader ────────────────────────────────────────────────────────────

interface CutsheetResponsePreviewProps {
  responseId: string;
  vendorId: string;
  onClose: () => void;
}

/**
 * Loads response data and opens a read-only preview modal via `useModal`.
 * Renders nothing visible itself — the preview appears inside the shared modal.
 * @param root0
 * @param root0.responseId
 * @param root0.vendorId
 * @param root0.onClose
 */
export function CutsheetResponsePreview({
  responseId,
  vendorId,
  onClose,
}: CutsheetResponsePreviewProps) {
  const { info } = useModal();

  const { data: responseData, loading: responseLoading } =
    useCutsheetQuestionnaireResponseByIdQuery({
      variables: { _id: responseId },
      skip: !responseId,
      fetchPolicy: 'cache-and-network',
    });

  const response = responseData?.cutsheetQuestionnaireResponseById;

  const { questionnaire, loading: qLoading } = useQuestionnaireState({
    questionnaireId: response ? String(response.questionnaire) : undefined,
  });

  const { catalog, loading: catalogLoading } = useCatalogState({
    vendorId,
    capabilityId: questionnaire?.capabilityId ?? '',
  });

  const isReady =
    !responseLoading &&
    !qLoading &&
    !catalogLoading &&
    !!response &&
    !!questionnaire &&
    !!catalog;

  useEffect(() => {
    if (!isReady) return;

    info({
      type: 'info',
      title: response!.name,
      size: 'lg',
      onCancel: onClose,
      body: (
        <ResponsePreviewContent
          response={response!}
          catalog={catalog!}
          questionnaire={questionnaire!}
        />
      ),
    });
    // We only want to open the modal once when data becomes ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  if (!isReady) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'background.paper',
          borderRadius: 2,
          px: 2,
          py: 1,
          boxShadow: 3,
        }}
      >
        <CircularProgress size={16} />
        <Typography variant="caption">Loading preview…</Typography>
      </Box>
    );
  }

  return null;
}
